import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import logoImg from '../images/logo.jpg';
import './Navbar.css';

const NAV_LINKS = [
  { to: '/', label: 'Home', end: true },
  { to: '/about', label: 'About' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/news', label: 'News & Events' },
  { to: '/admissions', label: 'Admissions' },
];

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const closeMenu = useCallback(() => setIsOpen(false), []);

  // Close menu on route change
  useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 900) setIsOpen(false);
    };
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <header className={`navbar${scrolled ? ' navbar--scrolled' : ''}${isOpen ? ' navbar--drawer-open' : ''}`} role="banner">

      {/* Brand */}
      <Link to="/" className="navbar-brand" aria-label="Twinkle Toes Schools — Home" onClick={closeMenu}>
        <img src={logoImg} alt="Twinkle Toes Schools logo" width="48" height="48" />
        <span className="navbar-brand-name">Twinkle Toes Schools</span>
      </Link>

      {/* Desktop nav — hidden on mobile */}
      <nav className="navbar-desktop" aria-label="Main navigation">
        <ul className="navbar-links">
          {NAV_LINKS.map((link) => (
            <li key={link.to}>
              <NavLink
                to={link.to}
                end={link.end}
                className={({ isActive }) => isActive ? 'active' : ''}
              >
                {link.label}
              </NavLink>
            </li>
          ))}
        </ul>
        <Link to="/contact" className="navbar-contact-btn">
          Contact Us
        </Link>
      </nav>

      {/* Hamburger — mobile only */}
      <button
        className={`hamburger${isOpen ? ' hamburger--active' : ''}`}
        onClick={() => setIsOpen((v) => !v)}
        aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={isOpen}
      >
        <span className="hamburger-line" aria-hidden="true" />
        <span className="hamburger-line" aria-hidden="true" />
        <span className="hamburger-line" aria-hidden="true" />
      </button>

      {/* Mobile drawer */}
      <div className={`navbar-drawer${isOpen ? ' navbar-drawer--open' : ''}`} aria-hidden={!isOpen}>
        <div className="navbar-drawer-brand">
          <img src={logoImg} alt="" width="36" height="36" />
          <span>Twinkle Toes</span>
        </div>
        <ul className="navbar-drawer-links">
          {NAV_LINKS.map((link) => (
            <li key={link.to}>
              <NavLink
                to={link.to}
                end={link.end}
                className={({ isActive }) => isActive ? 'active' : ''}
                onClick={closeMenu}
                tabIndex={isOpen ? 0 : -1}
              >
                {link.label}
              </NavLink>
            </li>
          ))}
        </ul>
        <Link
          to="/contact"
          className="navbar-contact-btn navbar-drawer-cta"
          onClick={closeMenu}
          tabIndex={isOpen ? 0 : -1}
        >
          Contact Us
        </Link>
      </div>

      {/* Backdrop */}
      {isOpen && (
        <div className="nav-backdrop" onClick={closeMenu} aria-hidden="true" />
      )}
    </header>
  );
}

export default Navbar;
